const CHANNEL_NAME = "metalui:sales-lifecycle";
const STORAGE_KEY = "metalui:sales-lifecycle-event";

type SalesLifecycleMessage = {
  type: "sales-lifecycle-changed";
  saleOrderIds: string[];
  sourceId?: string;
  nonce?: string;
};

const SOURCE_ID =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `sales-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function normalizedSaleOrderIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value
    .filter((id): id is string => typeof id === "string")
    .map((id) => id.trim())
    .filter(Boolean))];
}

function readMessage(value: unknown): SalesLifecycleMessage | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<SalesLifecycleMessage>;
  if (candidate.type !== "sales-lifecycle-changed") return null;
  const saleOrderIds = normalizedSaleOrderIds(candidate.saleOrderIds);
  return saleOrderIds.length
    ? {
        type: candidate.type,
        saleOrderIds,
        ...(typeof candidate.sourceId === "string" ? { sourceId: candidate.sourceId } : {}),
      }
    : null;
}

export function publishSalesLifecycleChange(
  saleOrderIds: string[],
  options: { skipCurrentTab?: boolean } = {},
): void {
  if (typeof window === "undefined") return;
  const ids = normalizedSaleOrderIds(saleOrderIds);
  if (!ids.length) return;
  const message: SalesLifecycleMessage = {
    type: "sales-lifecycle-changed",
    saleOrderIds: ids,
    ...(options.skipCurrentTab ? { sourceId: SOURCE_ID } : {}),
  };

  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
      return;
    }
  } catch {
    // A blocked channel falls back to the browser storage event below.
  }

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...message, nonce: crypto.randomUUID() }),
    );
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // A failed refresh hint must not turn a committed scan into a UI failure.
  }
}

export function subscribeSalesLifecycleChanges(
  onChange: (saleOrderIds: string[]) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handle = (value: unknown) => {
    const message = readMessage(value);
    if (message && message.sourceId !== SOURCE_ID) onChange(message.saleOrderIds);
  };

  let channel: BroadcastChannel | undefined;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.addEventListener("message", (event: MessageEvent<unknown>) => handle(event.data));
    }
  } catch {
    channel?.close();
    channel = undefined;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      handle(JSON.parse(event.newValue));
    } catch {
      // Ignore malformed cross-tab messages.
    }
  };
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener("storage", handleStorage);
    channel?.close();
  };
}
